import React from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Accordion,
  AccordionSummary,
  Button,
  Stack,
  TextField,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';

export const InsidePageContainer = styled(Stack)({
  padding: '0px 15px',
  margin: '0 auto',
  [`@media (min-width: 768px)`]: {
    width: '750px',
  },
  [`@media (min-width: 992px)`]: {
    width: '970px',
  },
  [`@media (min-width: 1280px)`]: {
    width: '1280px',
  },
});

export const AllinedPageContainer = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  minHeight: 'calc(100vh - 376px)',
  height: '100%',
  padding: '30px 0px',
  [`@media (max-width:${theme.breakpoints.values.lg}px)`]: {
    padding: '40px 0px',
  },
}));

export const PageContainer = styled(Stack)(({ theme, forcePaddingBottom }) => ({
  backgroundColor: theme.palette.grey[50],
  padding: '40px 10px',
  minHeight: 'calc(100vh - 120px)',
  height: '100%',
  [`@media (max-width:${theme.breakpoints.values.lg}px)`]: {
    padding: '30px',
    ...(forcePaddingBottom && {
      paddingBottom: 180,
    }),
  },
}));

export const DateButton = styled(Button)(({ selected }) => ({
  display: 'flex',
  padding: '10px 20px',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '5px',
  ...(selected
    ? { color: 'white', backgroundColor: '#DB0D00' }
    : {
        color: '#DB0D00',
        backgroundColor: '#F3F3F3',
        '&:hover': {
          color: 'white',
        },
      }),
}));

export const DateCheckboxButton = styled(
  ({ checked, onChange, label, ...props }) => (
    <FormControlLabel
      {...props}
      control={
        <Checkbox
          checked={checked}
          onChange={onChange}
          sx={{ display: 'none' }}
        />
      }
      label={
        <Stack direction="row" alignItems="center" gap={1}>
          <span>{label}</span>
          {checked && <CheckOutlinedIcon fontSize="small" />}
        </Stack>
      }
    />
  )
)(({ checked }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 20px',
  borderRadius: '4px',
  minWidth: 120,
  gap: '5px',
  backgroundColor: checked ? '#DB0D00' : '#F3F3F3',
  color: checked ? 'white' : '#DB0D00',
  border: 'none',
  fontFamily: 'Suisse-Intl',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  userSelect: 'none',
  '& .MuiCheckbox-root': {
    display: 'none',
  },
  '& .MuiFormControlLabel-label': {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
}));

export const BackButton = ({ backFn }) => {
  const intl = useIntl();
  const i18nCommon = translations.TicketsV2(intl).Common;
  return (
    <Stack
      direction={'row'}
      justifyContent={'space-between'}
      alignItems={'center'}
      gap={'10px'}
      sx={{ cursor: 'pointer' }}
      onClick={() => backFn()}
    >
      <ArrowBackIcon />
      <Typography variant="link1" sx={{ textDecoration: 'underline' }}>
        {i18nCommon.backBtn}
      </Typography>
    </Stack>
  );
};

export const RedLinkButton = ({
  icon,
  onClick,
  msg,
  hideTextOnMobile = false,
  marginTop = null,
}) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Button
      onClick={onClick}
      variant="text"
      color="primary"
      sx={{
        padding: 0,
        textTransform: 'none',
        marginTop: marginTop,
        '&:hover': {
          backgroundColor: 'transparent',
          textDecoration: 'underline',
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap="10px"
        justifyContent="center"
      >
        {!hideTextOnMobile || !isSmDown ? (
          <Typography
            variant="link1"
            sx={{ textDecoration: 'underline' }}
            color="primary"
          >
            {msg}
          </Typography>
        ) : null}
        {icon}
      </Stack>
    </Button>
  );
};

export const SdmTextfieldV5 = styled(TextField)({
  width: '90%',
  '& .disabled': {
    color: `#999`,
  },
  '& .MuiFormControl-root': {
    width: '95%',
  },
  '& label.Mui-focused': {
    color: `#333333`,
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: `#333333`,
  },
  '& .MuiInput-underline.Mui-disabled:before': {
    borderBottomStyle: `solid`,
    borderBottomColor: '#919EAB',
  },
  '& .MuiInput-underline:before': {
    borderBottom: `2px solid #333333`,
  },
  '& .MuiInputBase-input': {
    fontFamily: 'Suisse-Intl',
    fontSize: '20px',
    lineHeight: '28px',
    color: '#333333',
  },
  '& .MuiFormLabel-root': {
    fontFamily: 'Suisse-Intl',
    color: '#333333',
    fontSize: '21px',
  },
});

export const AccountChip = styled('div')({
  display: 'flex',
  padding: '5px 10px',
  alignItems: 'center',
  gap: '10px',
  borderRadius: 25,
  backgroundColor: '#E1E1E1',
});

export const StyledAccordion = styled(Accordion)(({ showBefore }) => ({
  borderRadius: '0 !important',
  boxShadow: 'none !important',
  '&.Mui-expanded': {
    marginTop: 0,
    ...(showBefore && {
      '&::before': {
        display: 'block !important',
        opacity: '1 !important',
      },
    }),
  },
}));

export const OrderStatusLabel = styled('div')(
  ({ bgColor, minWidth = null }) => ({
    display: 'flex',
    alignItems: 'center',
    borderRadius: '25px',
    justifyContent: 'center',
    gap: 5,
    backgroundColor: bgColor,
    padding: '13px',
    ...(minWidth && { minWidth: minWidth }),
  })
);

export const StyledAccordionSummary = styled(AccordionSummary)({
  '&::before': {
    display: 'block !important',
  },
});
